import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { fetchOrderDetails, updateTrackingUrl } from "../../../slice/order/orderSlice";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import { toast } from "react-toastify";

const OrderDetails = () => {
  const [trackingUrl, setTrackingUrl] = useState("");
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const orderDetailsRef = useRef(null);

  const { orderDetails, loading, error } = useSelector((state) => state.orders);

  useEffect(() => { dispatch(fetchOrderDetails(id)); }, [dispatch, id]);
  useEffect(() => { if (orderDetails?.trackingUrl) setTrackingUrl(orderDetails.trackingUrl); }, [orderDetails]);

  /* ── helpers ── */
  const isManual = orderDetails?.orderId?.startsWith("MO") || orderDetails?.user === null;
  const getStatusBadge = (status) => {
    const s = status?.toLowerCase();
    if (s === "delivered")  return "badge bg-success-subtle text-success px-2 py-1 fs-13";
    if (s === "processing") return "badge bg-warning-subtle text-warning px-2 py-1 fs-13";
    if (s === "pending")    return "badge bg-info-subtle text-info px-2 py-1 fs-13";
    if (s === "cancelled")  return "badge bg-danger-subtle text-danger px-2 py-1 fs-13";
    return "badge bg-primary-subtle text-primary px-2 py-1 fs-13";
  };

  const getPaymentStatusBadge = (ps) =>
    ps?.toLowerCase() === "paid"
      ? "badge bg-success-subtle text-success px-2 py-1 fs-13"
      : "badge bg-warning-subtle text-warning px-2 py-1 fs-13";

  /* ── downloads ── */
  const downloadExcel = () => {
    if (!orderDetails) return;
    const { user, items, shippingAddress, payment, subtotal, discount, shipping, totalAmount, status, createdAt } = orderDetails;

    const summaryData = [
      ["ORDER DETAILS", ""],
      ["Order ID",       orderDetails.orderId],
      ["Order Date",     new Date(createdAt).toLocaleDateString("en-US")],
      ["Order Status",   status?.toUpperCase()],
      ["Payment Status", payment.status?.toUpperCase()],
      ["Payment Method", payment.method || "Online Payment"],
      ["Transaction ID", payment.razorpayOrderId || "N/A"],
      [""],
      ["CUSTOMER DETAILS", ""],
      ["Customer Name", user?.name || shippingAddress?.fullName],
      ["Email",         user?.email || "—"],
      ["Phone",         shippingAddress?.phone],
      [""],
      ["SHIPPING ADDRESS", ""],
      ["Name",    shippingAddress?.fullName],
      ["Address", shippingAddress?.address],
      ["City/State/Pincode", `${shippingAddress?.district}, ${shippingAddress?.state} - ${shippingAddress?.pincode}`],
    ];

    const itemsData = [
      [""], ["ORDER ITEMS", "", "", "", "", ""],
      ["Product Name", "Frame Size", "Frame Color", "Texts", "Quantity", "Price"],
    ];
    items.forEach((item) => itemsData.push([
      item.name,
      item.frameSize || "—",
      item.frameColor || "—",
      item.texts?.join(", ") || "—",
      item.quantity,
      `₹${item.totalPrice?.toLocaleString()}`,
    ]));

    const totalsData = [
      [""], ["ORDER SUMMARY", ""],
      ["Sub Total",       `₹${subtotal?.toLocaleString()}`],
      ["Discount",        `-₹${discount?.toLocaleString()}`],
      ["Delivery Charge", `₹${shipping?.toLocaleString()}`],
      ["Total Amount",    `₹${totalAmount?.toLocaleString()}`],
    ];

    const ws = XLSX.utils.aoa_to_sheet([...summaryData, ...itemsData, ...totalsData]);
    ws["!cols"] = [{ wch: 25 }, { wch: 30 }, { wch: 15 }, { wch: 20 }, { wch: 10 }, { wch: 15 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Order Details");
    XLSX.writeFile(wb, `Order_${orderDetails.orderId || id}_Details.xlsx`);
  };

  const downloadPDF = () => {
    if (!orderDetails) return;
    const { user, items, shippingAddress, payment, subtotal, discount, shipping, totalAmount, status, createdAt } = orderDetails;
    const doc = new jsPDF();
    const pageWidth  = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 20;

    doc.setFontSize(22); doc.setFont("helvetica", "bold");
    doc.text("ORDER DETAILS", pageWidth / 2, yPos, { align: "center" });
    yPos += 12;

    doc.setFontSize(10); doc.setFont("helvetica", "normal");
    doc.text(`Order ID: ${orderDetails.orderId || id}`, 15, yPos);
    doc.text(`Date: ${new Date(createdAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}`, pageWidth - 15, yPos, { align: "right" });
    yPos += 8;
    doc.setFont("helvetica", "bold");
    doc.text(`Status: ${status?.toUpperCase()} | Payment: ${payment.status?.toUpperCase()}`, 15, yPos);
    yPos += 15;

    const leftColX = 15, rightColX = pageWidth / 2 + 5, colStartY = yPos;
    doc.setFontSize(12); doc.setFont("helvetica", "bold");
    doc.text("Customer Details", leftColX, yPos); yPos += 7;
    doc.setFontSize(10); doc.setFont("helvetica", "normal");
    doc.text(`Name: ${user?.name || shippingAddress?.fullName || "—"}`, leftColX, yPos); yPos += 5;
    doc.text(`Email: ${user?.email || "—"}`, leftColX, yPos); yPos += 5;
    doc.text(`Phone: ${shippingAddress?.phone || "—"}`, leftColX, yPos);

    yPos = colStartY;
    doc.setFontSize(12); doc.setFont("helvetica", "bold");
    doc.text("Shipping Address", rightColX, yPos); yPos += 7;
    doc.setFontSize(10); doc.setFont("helvetica", "normal");
    doc.splitTextToSize(shippingAddress?.fullName || "", pageWidth / 2 - 20).forEach(l => { doc.text(l, rightColX, yPos); yPos += 5; });
    doc.splitTextToSize(shippingAddress?.address  || "", pageWidth / 2 - 20).forEach(l => { doc.text(l, rightColX, yPos); yPos += 5; });
    doc.text(`${shippingAddress?.district}, ${shippingAddress?.state}`, rightColX, yPos); yPos += 5;
    doc.text(`PIN: ${shippingAddress?.pincode}`, rightColX, yPos);
    yPos = Math.max(yPos, colStartY + 30) + 10;

    autoTable(doc, {
      startY: yPos,
      head: [["Product", "Size", "Color", "Texts", "Qty", "Price"]],
      body: items.map(item => [
        item.name,
        item.frameSize  || "—",
        item.frameColor || "—",
        item.texts?.join(", ") || "—",
        item.quantity.toString(),
        `₹${item.totalPrice?.toLocaleString()}`,
      ]),
      theme: "striped",
      headStyles: { fillColor: [66, 139, 202], fontSize: 10, fontStyle: "bold" },
      columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 20 }, 2: { cellWidth: 25 }, 3: { cellWidth: 30 }, 4: { cellWidth: 15, halign: "center" }, 5: { cellWidth: 30, halign: "right" } },
      styles: { cellPadding: 4, fontSize: 9 },
      margin: { left: 15, right: 15 },
    });

    yPos = doc.lastAutoTable.finalY + 15;
    if (yPos > pageHeight - 60) { doc.addPage(); yPos = 20; }

    const sbX = pageWidth - 85, sbY = yPos, sbW = 70;
    doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.5);
    doc.rect(sbX, sbY, sbW, 45);
    yPos = sbY + 8;
    doc.setFontSize(12); doc.setFont("helvetica", "bold");
    doc.text("Order Summary", sbX + 5, yPos); yPos += 8;
    doc.setFontSize(9); doc.setFont("helvetica", "normal");
    doc.text("Sub Total:", sbX + 5, yPos); doc.text(`₹${subtotal?.toLocaleString()}`, sbX + sbW - 5, yPos, { align: "right" }); yPos += 6;
    doc.text("Discount:", sbX + 5, yPos); doc.setTextColor(220, 53, 69); doc.text(`-₹${discount?.toLocaleString()}`, sbX + sbW - 5, yPos, { align: "right" }); doc.setTextColor(0, 0, 0); yPos += 6;
    doc.text("Delivery:", sbX + 5, yPos); doc.text(`₹${shipping?.toLocaleString()}`, sbX + sbW - 5, yPos, { align: "right" }); yPos += 8;
    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text("Total Amount:", sbX + 5, yPos); doc.text(`₹${totalAmount?.toLocaleString()}`, sbX + sbW - 5, yPos, { align: "right" });

    yPos = sbY + 50;
    doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.text("Payment Information", 15, yPos); yPos += 6;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    doc.text(`Method: ${payment.method || "Online Payment"}`, 15, yPos); yPos += 5;
    doc.text(`Status: ${payment.status?.toUpperCase()}`, 15, yPos);
    if (payment.razorpayOrderId) { yPos += 5; doc.text(`Transaction ID: ${payment.razorpayOrderId}`, 15, yPos); }

    doc.setFontSize(8); doc.setTextColor(128, 128, 128);
    doc.text(`Generated on ${new Date().toLocaleDateString("en-US")}`, pageWidth / 2, pageHeight - 10, { align: "center" });
    doc.save(`Order_${orderDetails.orderId || id}_Details.pdf`);
  };

  const downloadImage = async () => {
    if (!orderDetailsRef.current) return;
    try {
      const buttons = orderDetailsRef.current.querySelectorAll(".download-buttons");
      buttons.forEach(b => b.style.display = "none");
      const canvas = await html2canvas(orderDetailsRef.current, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
      buttons.forEach(b => b.style.display = "");
      const link = document.createElement("a");
      link.download = `Order_${orderDetails.orderId || id}_Details.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      console.error(err);
      orderDetailsRef.current.querySelectorAll(".download-buttons").forEach(b => b.style.display = "");
    }
  };

  const handleTrackingUpdate = async () => {
    if (!trackingUrl.trim()) { toast.error("Please enter tracking URL"); return; }
    try {
      await dispatch(updateTrackingUrl({ orderId: id, trackingUrl })).unwrap();
      toast.success("Tracking URL updated successfully");
    } catch {
      toast.error("Failed to update tracking URL");
    }
  };

  /* ── loading / error states ── */
  if (loading) return (
    <div className="card"><div className="card-body text-center py-5">
      <div className="spinner-border text-primary mb-3" role="status" />
      <p className="text-muted">Loading order details...</p>
    </div></div>
  );

  if (error) return (
    <div className="card border-danger"><div className="card-body text-center py-5">
      <h4 className="text-danger">Error Loading Order</h4>
      <p className="text-muted mb-4">{error}</p>
      <button onClick={() => navigate(-1)} className="btn btn-primary">Go Back</button>
    </div></div>
  );

  if (!orderDetails) return null;

  const { user, items, shippingAddress, payment, subtotal, discount, shipping, totalAmount, status, createdAt } = orderDetails;

  /* Customer display name — manual orders have user:null */
  const customerName  = user?.name  || shippingAddress?.fullName || "—";
  const customerEmail = user?.email || "—";
  const customerPhone = shippingAddress?.phone || "—";

  return (
    <div className="row" ref={orderDetailsRef}>

      {/* Top bar */}
      <div className="callbackmain">
        <div className="cardBACK">
          <button onClick={() => navigate(-1)} className="btn btn-outline-secondary">Back</button>
          <div className="d-flex gap-2 download-buttons">
            <button onClick={downloadPDF}   className="btn btn-outline-danger"><i className="ri-file-pdf-line me-1" /> PDF</button>
            <button onClick={downloadExcel} className="btn btn-outline-success"><i className="ri-file-excel-line me-1" /> Excel</button>
            <button onClick={downloadImage} className="btn btn-outline-info"><i className="ri-image-line me-1" /> Image</button>
          </div>
        </div>
      </div>

      {/* ── LEFT COLUMN ── */}
      <div className="col-lg-8">

        {/* Header card */}
        <div className="card">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between">
              <h4>
                Order ID: {orderDetails?.orderId}{" "}
                {isManual && (
                  <span className="badge bg-info text-dark px-2 py-1 fs-13 me-1">Manual</span>
                )}
                <span className={getPaymentStatusBadge(payment.status)}>{payment.status?.toUpperCase()}</span>{" "}
                <span className={getStatusBadge(status)}>{status?.toUpperCase()}</span>
              </h4>
            </div>
            <div className="row g-3 mt-2">
              <div className="col-md-4">
                <p className="text-muted mb-1 fw-medium">Customer Name</p>
                <p className="mb-0">{customerName}</p>
              </div>
              <div className="col-md-4">
                <p className="text-muted mb-1 fw-medium">Order Date</p>
                <p className="mb-0">{new Date(createdAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Order Items card */}
        <div className="card">
          <div className="card-header"><h5 className="card-title mb-0">Order Items</h5></div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Image</th>
                    <th>Product Name</th>
                    {!isManual && <th>Frame Details</th>}
                    {!isManual && <th>Texts</th>}
                    <th>Quantity</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => (
                    <tr key={i}>

                      {/* Image column — for both regular and manual orders */}
                      <td>
                        {!isManual ? (
                          <div className="d-flex align-items-center gap-2">
                            {item.images?.map((img, idx) => (
                              <div key={idx} style={{ textAlign: "center" }}>
                                <div style={{ position: "relative", width: 60, height: 60 }}>
                                  <img
                                    src={`${process.env.REACT_APP_API_URL}${img}`}
                                    alt={`img-${idx}`}
                                    className="rounded"
                                    style={{ width: 60, height: 60, objectFit: "cover", cursor: "pointer" }}
                                    onClick={() => window.open(`${process.env.REACT_APP_API_URL}/uploads/${img}`, "_blank")}
                                  />
                                  <div
                                    style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 4, opacity: 0, transition: "opacity .2s" }}
                                    onMouseEnter={e => e.currentTarget.style.opacity = 1}
                                    onMouseLeave={e => e.currentTarget.style.opacity = 0}
                                    onClick={() => window.open(`${process.env.REACT_APP_API_URL}/uploads/${img}`, "_blank")}
                                  >
                                    <i className="ri-eye-line text-white" style={{ fontSize: 18 }} />
                                  </div>
                                </div>
                                <button
                                  className="btn btn-sm btn-outline-primary download-buttons mt-1"
                                  style={{ width: 60, fontSize: 11, padding: "2px 4px" }}
                                  onClick={() => {
                                    fetch(`${process.env.REACT_APP_API_URL}/uploads/${img}`)
                                      .then(r => r.blob()).then(blob => {
                                        const url  = URL.createObjectURL(blob);
                                        const link = document.createElement("a");
                                        link.href = url; link.download = `image_${idx + 1}.png`;
                                        document.body.appendChild(link); link.click();
                                        document.body.removeChild(link); URL.revokeObjectURL(url);
                                      });
                                  }}
                                >Download</button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          // Manual order — image + download inside table cell
                          item.images?.[0] ? (
                            <div style={{ textAlign: "center" }}>
                              <div style={{ position: "relative", width: 60, height: 60 }}>
                                <img
                                  src={`${process.env.REACT_APP_API_URL}${item.images[0]}`}
                                  alt="reference"
                                  className="rounded"
                                  style={{ width: 60, height: 60, objectFit: "cover", cursor: "pointer", border: "1px solid #dee2e6" }}
                                  onClick={() => window.open(`${process.env.REACT_APP_API_URL}${item.images[0]}`, "_blank")}
                                />
                                <div
                                  style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 4, opacity: 0, transition: "opacity .2s" }}
                                  onMouseEnter={e => e.currentTarget.style.opacity = 1}
                                  onMouseLeave={e => e.currentTarget.style.opacity = 0}
                                  onClick={() => window.open(`${process.env.REACT_APP_API_URL}${item.images[0]}`, "_blank")}
                                >
                                  <i className="ri-eye-line text-white" style={{ fontSize: 18 }} />
                                </div>
                              </div>
                              <button
                                className="btn btn-sm btn-outline-primary download-buttons mt-1"
                                style={{ width: 60, fontSize: 11, padding: "2px 4px" }}
                                onClick={() => {
                                  fetch(`${process.env.REACT_APP_API_URL}${item.images[0]}`)
                                    .then(r => r.blob()).then(blob => {
                                      const url  = URL.createObjectURL(blob);
                                      const link = document.createElement("a");
                                      link.href = url; link.download = `reference_image.png`;
                                      document.body.appendChild(link); link.click();
                                      document.body.removeChild(link); URL.revokeObjectURL(url);
                                    });
                                }}
                              >Download</button>
                            </div>
                          ) : "—"
                        )}
                      </td>

                      <td>{item.product?.name || item.name || "—"}</td>

                      {/* Frame details — only for regular orders */}
                      {!isManual && (
                        <td>
                          <small className="text-muted d-block">Size: <span className="text-dark">{item.frameSize || "—"}</span></small>
                          <small className="text-muted d-block">Color:{" "}
                            <span className="text-dark">
                              {item.frameColor && (
                                <span style={{ display: "inline-block", width: 16, height: 16, borderRadius: "50%", backgroundColor: item.frameColor, border: "1px solid #ccc", marginLeft: 6, verticalAlign: "middle" }} />
                              )}
                              &nbsp;{item.frameColor || "—"}
                            </span>
                          </small>
                        </td>
                      )}

                      {/* Texts — only for regular orders */}
                      {!isManual && <td>{item.texts?.join(", ") || "—"}</td>}

                      <td>{item.quantity}</td>
                      <td>₹{item.totalPrice?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Customer Details card */}
        <div className="card">
          <div className="card-header"><h5 className="card-title mb-0">Customer Details</h5></div>
          <div className="card-body">
            <div className="row g-3 mt-2">
              <div className="col-md-6">
                <div className="d-flex align-items-center mb-3">
                  <div style={{ width: 48, height: 48, borderRadius: "50%", backgroundColor: "#405189", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 500, flexShrink: 0, marginRight: 12 }}>
                    {customerName?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h6 className="mb-1">{customerName}</h6>
                    <p className="text-muted mb-0">{customerEmail}</p>
                  </div>
                </div>

                <div className="mb-3">
                  <p className="text-muted mb-1 fw-medium">Contact Number</p>
                  <p className="mb-0">{customerPhone}</p>
                </div>

                <div>
                  <p className="text-muted mb-1 fw-medium">Shipping Address</p>
                  <address className="mb-0">
                    <strong>{shippingAddress?.fullName}</strong><br />
                    {shippingAddress?.address}<br />
                    {shippingAddress?.district}, {shippingAddress?.state} — {shippingAddress?.pincode}<br />
                    Phone: {shippingAddress?.phone}
                    {shippingAddress?.notes && <><br /><em>Notes: {shippingAddress.notes}</em></>}
                  </address>
                </div>
              </div>

              <div className="col-md-6 Cusflex">
                <div className="updateuRl">
                  <label className="form-label fw-medium">Tracking URL</label>
                  <input
                    type="text" className="form-control mb-2"
                    placeholder="Enter Tracking URL"
                    value={trackingUrl}
                    onChange={(e) => setTrackingUrl(e.target.value)}
                  />
                  <button className="btn btn-primary w-100" onClick={handleTrackingUpdate}>
                    Update Tracking URL
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── RIGHT COLUMN ── */}
      <div className="col-lg-4">

        {/* Order Summary */}
        <div className="card">
          <div className="card-header"><h5 className="card-title mb-0">Order Summary</h5></div>
          <div className="card-body">
            <table className="table table-borderless mb-0">
              <tbody>
                <tr><td className="text-muted">Sub Total :</td><td className="text-end">₹{subtotal?.toLocaleString()}</td></tr>
                <tr><td className="text-muted">Discount :</td><td className="text-end text-danger">-₹{discount?.toLocaleString()}</td></tr>
                <tr><td className="text-muted">Delivery Charge :</td><td className="text-end">₹{shipping?.toLocaleString()}</td></tr>
                <tr className="border-top"><th className="pt-2">Total Amount</th><th className="text-end pt-2">₹{totalAmount?.toLocaleString()}</th></tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Payment Info */}
        <div className="card">
          <div className="card-header"><h5 className="card-title mb-0">Payment Information</h5></div>
          <div className="card-body">
            <div className="mb-3">
              <p className="text-muted mb-1 fw-medium">Payment Method</p>
              <p className="mb-0 text-uppercase">
                {isManual
                  ? (payment.status === "paid" ? "Paid" : "Online Payment")
                  : (payment.method || "Online Payment")}
              </p>
            </div>
            <div className="mb-3">
              <p className="text-muted mb-1 fw-medium">Payment Status</p>
              <span className={getPaymentStatusBadge(payment.status)}>{payment.status?.toUpperCase()}</span>
            </div>
            {isManual && (
              <>
                {shippingAddress?.notes && (
                  <div className="mt-3">
                    <p className="text-muted mb-1 fw-medium">Order Notes</p>
                    <p className="mb-0">{shippingAddress.notes}</p>
                  </div>
                )}
              </>
            )}
            {payment.status?.toLowerCase() === "paid" && (
              <div className="alert alert-success mb-3">
                <i className="ri-checkbox-circle-line me-2" />Payment successfully processed
              </div>
            )}
            {payment.razorpayOrderId && (
              <><hr /><p className="text-muted mb-1 fw-medium">Transaction ID: <span className="text-dark fw-normal">{payment.razorpayOrderId}</span></p></>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default OrderDetails;